import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, X, Check, ShoppingCart, HelpCircle, Sparkles, Star } from 'lucide-react';
import { Badge } from '../Badge/Badge';
import { CategoryFilter } from '../CategoryFilter/CategoryFilter';
import type { CategoryFilterItem } from '../CategoryFilter/CategoryFilter';
import { useProductStore, type ProductInput } from '@/features/catalogo-articulos/hooks/useProductStore';
import ProductModal from '@/components/Producto/ModalProducto';
import { type Product as CatalogProduct } from '@/features/catalogo-articulos/models/types';
import { usePriceBook } from '@/shared/Comprobante/form-core/hooks/usePriceBook';
import { useCurrentEstablecimientoId, useUserSession } from '@/contexts/UserSessionContext';
import { useConfigurationContext } from '@/contexts/ContextoConfiguracion';
import { getAvailableStockForUnit } from '@/shared/inventory/stockGateway';
import { registrarProductoCreadoExitoso } from '@/shared/analitica/analitica';
import { useOnboardingStore } from '@/features/checklist-inicio/store/useOnboardingStore';
import { useProductSearch } from '@/hooks/productos/useProductSearch';
import { productosService, parseJsonSafe, productoInputADto } from '@/services/api/productos.service';
import type { ProductoBuscarDto, ProductoInputDto } from '@/services/api/productos.service';
import { CATALOGO_54_DETRACCIONES } from '@/shared/catalogos-sunat';
import {
  type ScanBurstState,
  createScanBurstState,
  normalizeScanKey,
  looksLikeScanCode,
  SCAN_IDLE_THRESHOLD_MS,
  SCAN_BURST_MAX_DURATION_MS,
  SCAN_AUTO_MIN_LENGTH,
} from '@/shared/scanner/barcodeScanHeuristics';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface SelectordeProductosProduct {
  id: string;
  code: string;
  name: string;
  price: number;
  stock: number;
  category: string;
  categoryId?: string;
  priceType?: string;
  requiresStockControl?: boolean;
  tipoProducto?: 'BIEN' | 'SERVICIO';
  descripcion?: string;
  alias?: string;
  marca?: string;
  modelo?: string;
  codigoBarras?: string;
  codigoFabrica?: string;
  precioCompra?: number;
  descuentoProducto?: number;
  peso?: number;
  tipoExistencia?: string;
  impuesto?: string;
  afectacionTributariaId?: string;
  afectacionTributariaCodigo?: string;
  afectacionTributariaCodigoTributo?: string;
  afectacionTributariaImpuesto?: string;
  afectacionTributariaNombre?: string;
  imagen?: string;
  codigoSunat?: string;
  unidad?: string;
  unitSymbol?: string;
  unidadMedidaId?: string;
  unitName?: string;
  esDetraccion?: boolean;
  detraccionCodigo?: string;
  detraccionPorcentaje?: number;
}

export interface SelectordeProductosProps {
  onAddProducts: (productos: { product: SelectordeProductosProduct; quantity: number }[]) => void;
  existingProducts?: string[];
  dropdownTourId?: string;
  soloTipoProducto?: 'BIEN' | 'SERVICIO';
  /**
   * Contenido adicional a mostrar en la barra superior, a la izquierda del
   * toggle "Selección múltiple" (ej. un ícono de configuración externo).
   */
  extraTopbarContent?: React.ReactNode;
}

// ─── Estilos inyectados ────────────────────────────────────────────────────────

const STYLE_ID = 'ps-selector-styles';

const STYLES = `
.ps-root {
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  position: relative;
  width: 100%;
}
.ps-topbar { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
.ps-section-label { font-size:11px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:var(--text-tertiary); }
.ps-toggle-wrap { display:flex; align-items:center; gap:8px; cursor:pointer; user-select:none; }
.ps-toggle-text { font-size:12px; color:var(--text-tertiary); font-weight:500; transition:color .2s; }
.ps-toggle-text--on { color:var(--primary); }
.ps-toggle { width:36px; height:20px; background:var(--border-default); border-radius:10px; position:relative; transition:background .2s; flex-shrink:0; }
.ps-toggle--on { background:var(--primary); }
.ps-toggle-knob { position:absolute; top:3px; left:3px; width:14px; height:14px; background:white; border-radius:50%; box-shadow:0 1px 3px rgba(0,0,0,.2); transition:transform .2s cubic-bezier(.34,1.56,.64,1); }
.ps-toggle--on .ps-toggle-knob { transform:translateX(16px); }
.ps-input-wrap { position:relative; display:flex; align-items:center; }
.ps-input { width:100%; height:40px; padding:0 80px 0 18px; font-family:inherit; font-size:14px; color:var(--text-primary); background:var(--surface-0); border:1px solid var(--border-strong); border-radius:12px; outline:none; transition:border-color .2s, box-shadow .2s; }
.ps-input:hover { border-color:var(--border-strong); }
.ps-input:focus { border-color:var(--primary); box-shadow:0 0 0 2px rgba(22,98,197,.1); }
.ps-input::placeholder { color:var(--text-tertiary); opacity:0.7; }
.ps-input--open { border-bottom-left-radius:0; border-bottom-right-radius:0; border-bottom-color:transparent; border-color:var(--primary); }
.ps-input-actions { position:absolute; right:12px; display:flex; align-items:center; gap:6px; }
.ps-category-filter { padding:12px 16px 0; border-bottom:1px solid var(--border-muted); }
.ps-icon-btn { background:none; border:none; cursor:pointer; color:var(--text-tertiary); padding:0; display:flex; align-items:center; transition:color .15s; }
.ps-icon-btn:hover { color:var(--primary); }
.ps-chips { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:8px; }
.ps-dropdown { position:absolute; top:100%; left:0; right:0; background:var(--surface-0); border:1.5px solid var(--primary); border-top:none; border-bottom-left-radius:12px; border-bottom-right-radius:12px; box-shadow:0 10px 25px -5px rgba(0,0,0,.1), 0 4px 10px -5px rgba(0,0,0,.06); z-index:50; overflow:hidden; animation:ps-drop-in .15s ease; }
@keyframes ps-drop-in { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
.ps-dropdown-list { max-height:420px; overflow-y:auto; scrollbar-width:thin; scrollbar-color:var(--border-default) transparent; }
.ps-dropdown-header { display:flex; align-items:center; gap:8px; padding:10px 16px; background:var(--primary-light); border-bottom:1px solid #bfdbfe; font-size:12px; font-weight:600; color:var(--primary); }
.ps-row { display:flex; align-items:center; gap:12px; padding:12px 18px; cursor:pointer; border-bottom:1px solid var(--border-muted); transition:background .12s; }
.ps-row:last-child { border-bottom:none; }
.ps-row--hover { background:var(--surface-hover); }
.ps-row--selected { background:var(--surface-selected); }
.ps-stripe { width:3px; height:36px; border-radius:2px; background:var(--border-default); flex-shrink:0; transition:background .15s; }
.ps-row--hover .ps-stripe, .ps-row--selected .ps-stripe { background:var(--primary); }
.ps-check { width:18px; height:18px; border:1.5px solid var(--border-default); border-radius:5px; background:white; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:all .15s; color:white; }
.ps-check--on { background:var(--primary); border-color:var(--primary); }
.ps-row-body { flex:1; min-width:0; }
.ps-row-top { display:flex; align-items:baseline; justify-content:space-between; gap:8px; margin-bottom:5px; }
.ps-name { font-size:14px; font-weight:500; color:var(--text-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.ps-price { font-size:13px; font-weight:500; color:var(--primary); white-space:nowrap; flex-shrink:0; }
.ps-row-meta { display:flex; flex-wrap:wrap; gap:8px; align-items:center; }
.ps-badge { display:inline-flex; align-items:center; gap:3px; font-size:11px; font-weight:500; padding:2px 7px; border-radius:999px; }
.ps-badge--code { background:var(--surface-2); color:var(--text-secondary); }
.ps-badge--stock { background:transparent; padding-left:2px; }
.ps-badge--cart { background:#fef3c7; color:#92400e; }
.ps-dot { display:inline-block; width:6px; height:6px; border-radius:50%; flex-shrink:0; }
.ps-row-right { flex-shrink:0; display:flex; flex-direction:column; align-items:flex-end; gap:4px; }
.ps-qty { display:flex; align-items:center; background:white; border:1.5px solid #bfdbfe; border-radius:8px; overflow:hidden; }
.ps-qty-btn { width:24px; height:24px; background:none; border:none; cursor:pointer; color:var(--primary); display:flex; align-items:center; justify-content:center; font-size:16px; line-height:1; transition:background .12s; }
.ps-qty-btn:hover { background:var(--primary-light); }
.ps-qty-value { font-size:12px; font-weight:500; color:var(--text-primary); min-width:22px; text-align:center; }
.ps-subtotal { font-size:11px; color:var(--primary); font-weight:500; }
.ps-footer { padding:10px 14px; background:var(--surface-1); border-top:1px solid var(--border-default); }
.ps-add-btn { width:100%; height:42px; background:var(--primary); color:white; border:none; border-radius:10px; font-family:inherit; font-size:13px; font-weight:600; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:8px; transition:background .15s, transform .1s, box-shadow .15s; box-shadow:0 2px 8px rgba(22,98,197,.3); }
.ps-add-btn:hover:not(:disabled) { background:var(--primary-hover); transform:translateY(-1px); box-shadow:0 4px 12px rgba(22,98,197,.35); }
.ps-add-btn:active:not(:disabled) { transform:translateY(0); }
.ps-add-btn:disabled { opacity:.5; cursor:default; box-shadow:none; }
.ps-add-btn-badge { background:rgba(255,255,255,.25); width:20px; height:20px; border-radius:50%; font-size:11px; display:flex; align-items:center; justify-content:center; }
.ps-create-row { display:flex; align-items:center; gap:10px; padding:10px 18px; cursor:pointer; border-top:1px dashed var(--border-default); transition:background .12s; color:var(--primary); font-size:13px; font-weight:500; }
.ps-create-row:hover { background:var(--primary-light); }
.ps-create-row-icon { width:28px; height:28px; border-radius:8px; background:var(--primary-light); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.ps-no-results { padding:32px 24px; text-align:center; }
.ps-no-results-icon { width:48px; height:48px; background:var(--surface-2); border-radius:14px; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; color:var(--text-tertiary); }
.ps-no-results-title { font-size:14px; font-weight:600; color:var(--text-primary); margin:0 0 4px; }
.ps-no-results-sub { font-size:13px; color:var(--text-tertiary); margin:0 0 16px; }
.ps-create-btn { display:inline-flex; align-items:center; gap:7px; background:var(--primary-light); color:var(--primary); border:1.5px solid #bfdbfe; border-radius:10px; padding:8px 16px; font-family:inherit; font-size:13px; font-weight:600; cursor:pointer; transition:background .15s; }
.ps-create-btn:hover { background:#bfdbfe; }
.ps-fav-header { display:flex; align-items:center; gap:7px; padding:8px 16px 7px; font-size:11px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:var(--text-tertiary); border-bottom:1px solid var(--border-muted); }
.ps-fav-star { color:#f59e0b; flex-shrink:0; }
.ps-fav-name-star { display:inline-flex; align-items:center; gap:5px; min-width:0; overflow:hidden; }
`;

if (!document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = STYLES;
  document.head.appendChild(el);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (value: number) => `S/ ${(value ?? 0).toFixed(2)}`;

function stockTone(stock: number, isService: boolean) {
  if (isService) return null;
  if (stock <= 0) return { color: '#ef4444', label: 'Sin stock' };
  if (stock <= 10) return { color: '#f59e0b', label: `${stock} uds.` };
  return { color: '#10b981', label: `${stock} uds.` };
}

const CATEGORY_FALLBACK_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#14B8A6'];
function getCategoryFallbackColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return CATEGORY_FALLBACK_COLORS[h % CATEGORY_FALLBACK_COLORS.length];
}

function escapeRegex(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightMatch(text: string, term: string): React.ReactNode {
  if (!term.trim()) return text;
  const parts = text.split(new RegExp(`(${escapeRegex(term)})`, 'gi'));
  let cursor = 0;
  return parts.map((part) => {
    const key = `${part}-${cursor}`;
    cursor += part.length;
    return part.toLowerCase() === term.toLowerCase() ? (
      <mark
        key={key}
        style={{ background: '#fef08a', fontWeight: 600, borderRadius: 2, fontStyle: 'normal' }}
      >
        {part}
      </mark>
    ) : (
      <React.Fragment key={key}>{part}</React.Fragment>
    );
  });
}

// ─── Componente ───────────────────────────────────────────────────────────────

export const SelectordeProductos: React.FC<SelectordeProductosProps> = ({
  onAddProducts,
  existingProducts = [],
  dropdownTourId,
  soloTipoProducto,
  extraTopbarContent,
}) => {

  // ── Datos del catálogo ───────────────────────────────────────────────────────
  const { allProducts: catalogProducts, categories: catalogCategories, addProduct } = useProductStore();
  const { baseColumnId, getPriceOptionsFor, hasSelectableColumns } = usePriceBook();
  const EstablecimientoId = useCurrentEstablecimientoId();

  const { results: serverResults, isSearching, search: searchApi, clear: clearSearch } =
    useProductSearch({
      establecimientoId: EstablecimientoId ?? undefined,
      limit: 20,
      soloBienes: soloTipoProducto === 'BIEN',
    });
  const { session } = useUserSession();
  const { state: { almacenes, categories: configCategories } } = useConfigurationContext();

  const categoryColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const cat of configCategories) {
      if (cat.color && cat.nombre) map[cat.nombre.toLowerCase()] = cat.color;
    }
    return map;
  }, [configCategories]);

  const [activeCategoryIds, setActiveCategoryIds] = useState<Set<string>>(new Set());

  const activeCategoryNames = useMemo(() => {
    if (activeCategoryIds.size === 0) return null;
    return new Set(
      configCategories
        .filter(c => activeCategoryIds.has(c.id))
        .map(c => c.nombre.toLowerCase())
    );
  }, [activeCategoryIds, configCategories]);

  const filterCategories = useMemo<CategoryFilterItem[]>(() => {
    const countMap = new Map<string, number>();
    catalogProducts
      .filter(p => soloTipoProducto !== 'BIEN' || p.tipoExistencia !== 'SERVICIOS')
      .forEach(p => {
        if (p.categoria) {
          const key = p.categoria.toLowerCase();
          countMap.set(key, (countMap.get(key) ?? 0) + 1);
        }
      });
    return configCategories
      .filter(c => (countMap.get(c.nombre.toLowerCase()) ?? 0) > 0)
      .map(c => ({
        id: c.id,
        name: c.nombre,
        color: c.color ?? getCategoryFallbackColor(c.id),
        productCount: countMap.get(c.nombre.toLowerCase()) ?? 0,
      }));
  }, [configCategories, catalogProducts, soloTipoProducto]);

  const handleCategoryChange = useCallback((newIds: Set<string>) => {
    setActiveCategoryIds(newIds);
  }, []);

  const categoryFilterBar = filterCategories.length > 0 && (
    <CategoryFilter
      className="ps-category-filter"
      categories={filterCategories}
      activeIds={activeCategoryIds}
      onChange={handleCategoryChange}
      label=""
    />
  );

  const mapProduct = useCallback((p: CatalogProduct): SelectordeProductosProduct => {
    
    const stockInfo = (getAvailableStockForUnit as any)({
      product: p,
      almacenes,
      EstablecimientoId,
      unitCode: p.unidad,
    });
    const stock = typeof stockInfo === 'number'
      ? stockInfo
      : (stockInfo?.availableInUnidadSeleccionada ?? 0);

    const requiresStockControl = Boolean(
      p.stockPorAlmacen || p.stockPorEstablecimiento || typeof p.cantidad === 'number'
    );

    let price = p.precio;
    let priceType = 'Precio base';
    if (hasSelectableColumns) {
      const sku = p.codigo || p.id;
      const options = getPriceOptionsFor(sku, p.unidad);
      if (options.length > 0) {
        const preferred = options.find(o => o.isBase || (baseColumnId && o.columnId === baseColumnId));
        const chosen = preferred ?? options[0];
        price = chosen.price;
        priceType = chosen.label;
      }
    }

    return {
      id: p.id,
      code: p.codigo,
      name: p.nombre,
      price,
      stock,
      priceType,
      requiresStockControl,
      category: p.categoria || 'Sin categoría',
      categoryId: p.categoriaId,
      tipoProducto: p.tipoExistencia === 'SERVICIOS' ? 'SERVICIO' : 'BIEN',
      descripcion: p.descripcion,
      alias: p.alias,
      marca: p.marca,
      modelo: p.modelo,
      codigoBarras: p.codigoBarras,
      codigoFabrica: p.codigoFabrica,
      precioCompra: p.precioCompra,
      descuentoProducto: p.descuentoProducto,
      peso: p.peso,
      tipoExistencia: p.tipoExistencia,
      impuesto: p.impuesto,
      afectacionTributariaId: p.afectacionTributariaId ?? undefined,
      afectacionTributariaCodigo: p.afectacionTributariaCodigo ?? undefined,
      afectacionTributariaCodigoTributo: p.afectacionTributariaCodigoTributo ?? undefined,
      afectacionTributariaImpuesto: p.afectacionTributariaImpuesto ?? undefined,
      afectacionTributariaNombre: p.afectacionTributariaNombre ?? undefined,
      imagen: p.imagen,
      codigoSunat: p.codigoSunat,
      unidad: p.unidad,
      unitSymbol: p.unitSymbol,
      unidadMedidaId: p.unidadMedidaId,
      unitName: p.unitName,
      esDetraccion: p.esDetraccion ?? false,
      detraccionCodigo: p.detraccionCodigo ?? undefined,
      detraccionPorcentaje: p.detraccionPorcentaje ?? undefined,
    };
  }, [EstablecimientoId, almacenes, baseColumnId, getPriceOptionsFor, hasSelectableColumns]);

  const mapBuscarDto = useCallback((dto: ProductoBuscarDto): SelectordeProductosProduct => {
    let price = dto.precio;
    let priceType = 'Precio base';
    if (hasSelectableColumns) {
      const sku = dto.codigo || dto.id;
      const options = getPriceOptionsFor(sku, dto.unidadMedidaCodigoSunat ?? undefined);
      if (options.length > 0) {
        const preferred = options.find(o => o.isBase || (baseColumnId && o.columnId === baseColumnId));
        const chosen = preferred ?? options[0];
        price = chosen.price;
        priceType = chosen.label;
      }
    }

    const stockPorAlmacenDto = parseJsonSafe<Record<string, number>>(dto.stockPorAlmacen ?? null, {});
    const stockReservadoPorAlmacenDto = parseJsonSafe<Record<string, number>>(dto.stockReservadoPorAlmacen ?? null, {});
    const stockInfo = (getAvailableStockForUnit as any)({
      product: {
        stockPorAlmacen: stockPorAlmacenDto,
        stockReservadoPorAlmacen: stockReservadoPorAlmacenDto,
        unidad: dto.unidadMedidaCodigoSunat,
      },
      almacenes,
      EstablecimientoId,
      unitCode: dto.unidadMedidaCodigoSunat,
    });
    const stock = typeof stockInfo === 'number'
      ? stockInfo
      : (stockInfo?.availableInUnidadSeleccionada ?? 0);

    return {
      id: dto.id,
      code: dto.codigo ?? '',
      name: dto.nombre,
      price,
      priceType,
      stock,
      requiresStockControl: dto.tipoExistencia !== 'SERVICIOS',
      category: dto.categoriaNombre ?? 'Sin categoría',
      categoryId: dto.categoriaId ?? undefined,
      tipoProducto: dto.tipoExistencia === 'SERVICIOS' ? 'SERVICIO' : 'BIEN',
      imagen: dto.imagenUrl ?? undefined,
      descripcion: dto.descripcion ?? undefined,
      alias: dto.alias ?? undefined,
      marca: dto.marca ?? undefined,
      modelo: dto.modelo ?? undefined,
      codigoBarras: dto.codigoBarras ?? undefined,
      codigoFabrica: dto.codigoFabrica ?? undefined,
      precioCompra: dto.precioCompra ?? undefined,
      descuentoProducto: dto.descuentoProducto ?? undefined,
      peso: dto.peso ?? undefined,
      tipoExistencia: dto.tipoExistencia ?? undefined,
      impuesto: dto.afectacionTributariaNombre ?? dto.afectacionTributariaCodigo ?? undefined,
      afectacionTributariaId: dto.afectacionTributariaId ?? undefined,
      afectacionTributariaCodigo: dto.afectacionTributariaCodigo ?? undefined,
      afectacionTributariaCodigoTributo: dto.afectacionTributariaCodigoTributo ?? undefined,
      afectacionTributariaImpuesto: dto.afectacionTributariaImpuesto ?? undefined,
      afectacionTributariaNombre: dto.afectacionTributariaNombre ?? undefined,
      codigoSunat: dto.codigoSunat ?? undefined,
      unidad: dto.unidadMedidaCodigoSunat ?? undefined,
      unitSymbol: dto.unidadMedidaSimbolo ?? undefined,
      unidadMedidaId: dto.unidadMedidaId ?? undefined,
      unitName: dto.unidadMedidaNombre ?? undefined,
      esDetraccion: dto.esDetraccion ?? false,
      detraccionCodigo: dto.detraccionCodigo ?? undefined,
      detraccionPorcentaje: dto.detraccionPorcentaje ?? undefined,
    };
  }, [baseColumnId, getPriceOptionsFor, hasSelectableColumns, almacenes, EstablecimientoId]);

  const productLookupByCode = useMemo(() => {
    const lookup = new Map<string, CatalogProduct>();
    catalogProducts
      .filter(p => soloTipoProducto !== 'BIEN' || p.tipoExistencia !== 'SERVICIOS')
      .forEach(p => {
        const barcodeKey = normalizeScanKey(p.codigoBarras);
        if (barcodeKey) {
          lookup.set(barcodeKey, p);
        }
        const codeKey = normalizeScanKey(p.codigo);
        if (codeKey) {
          lookup.set(codeKey, p);
        }
      });
    return lookup;
  }, [catalogProducts, soloTipoProducto]);

  // ── Estado UI ───────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm]         = useState('');
  const [isMultipleMode, setIsMultipleMode] = useState(false);
  const [showDropdown, setShowDropdown]     = useState(false);
  const [selectedIds, setSelectedIds]       = useState<Set<string>>(new Set());
  const [selectedProductsMap, setSelectedProductsMap] = useState<Map<string, SelectordeProductosProduct>>(new Map());
  const [quantities, setQuantities]         = useState<Record<string, number>>({});
  const [hoveredIndex, setHoveredIndex]     = useState(-1);
  const [showTooltip, setShowTooltip]       = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [productNamePrefill, setProductNamePrefill] = useState('');

  const containerRef    = useRef<HTMLDivElement>(null);
  const inputRef        = useRef<HTMLInputElement>(null);
  const itemRefs        = useRef<Array<HTMLDivElement | null>>([]);
  const skipNextOpenRef = useRef(false);
  const scanBurstRef    = useRef<ScanBurstState>(createScanBurstState());

  const isEmptySearch = !searchTerm.trim();
  const hasActiveCategory = activeCategoryIds.size > 0;

  const filteredProducts = useMemo<SelectordeProductosProduct[]>(() => {
    if (!isEmptySearch) {
      return serverResults
        .map(mapBuscarDto)
        .filter(p => !activeCategoryNames || activeCategoryNames.has(p.category.toLowerCase()));
    }
    if (activeCategoryNames) {
      return catalogProducts
        .filter(p => soloTipoProducto !== 'BIEN' || p.tipoExistencia !== 'SERVICIOS')
        .map(mapProduct)
        .filter(p => activeCategoryNames.has(p.category.toLowerCase()));
    }
    return [];
  }, [isEmptySearch, serverResults, mapBuscarDto, activeCategoryNames, catalogProducts, mapProduct, soloTipoProducto]);

  const favoriteProducts = useMemo<SelectordeProductosProduct[]>(
    () => catalogProducts
      .filter(p => p.isFavorite)
      .filter(p => soloTipoProducto !== 'BIEN' || p.tipoExistencia !== 'SERVICIOS')
      .map(mapProduct),
    [catalogProducts, mapProduct, soloTipoProducto],
  );

  const showFavorites   = showDropdown && isEmptySearch && !hasActiveCategory && favoriteProducts.length > 0;
  const hasResults      = filteredProducts.length > 0;
  const showNoResult    = showDropdown && !isSearching && !hasResults && (searchTerm.trim().length > 0 || hasActiveCategory);

  // ── Click outside ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setShowDropdown(false);
        setHoveredIndex(-1);
      }
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [showDropdown]);

  // ── Teclado ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!showDropdown) return;
    const activeList = showFavorites ? favoriteProducts : filteredProducts;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHoveredIndex(i => Math.min(i + 1, activeList.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHoveredIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        if (hoveredIndex >= 0) {
          e.preventDefault();
          handleSelect(activeList[hoveredIndex]);
        } else if (!isEmptySearch && !isSearching && activeList.length === 0) {
          e.preventDefault();
          handleCreateClick();
        }
      } else if (e.key === 'Escape') {
        setShowDropdown(false);
        setHoveredIndex(-1);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);

  }, [showDropdown, hoveredIndex, filteredProducts, favoriteProducts, showFavorites, isEmptySearch, isSearching]);

  // ── Auto-scroll hovered ─────────────────────────────────────────────────────
  useEffect(() => {
    if (hoveredIndex >= 0) {
      itemRefs.current[hoveredIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [hoveredIndex]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleSelect = useCallback((product: SelectordeProductosProduct) => {
    if (isMultipleMode) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(product.id)) {
          next.delete(product.id);
          setSelectedProductsMap(m => { const nm = new Map(m); nm.delete(product.id); return nm; });
        } else {
          next.add(product.id);
          setQuantities(q => ({ ...q, [product.id]: q[product.id] ?? 1 }));
          setSelectedProductsMap(m => new Map(m).set(product.id, product));
        }
        return next;
      });
    } else {
      onAddProducts([{ product, quantity: 1 }]);
      setSearchTerm('');
      setShowDropdown(false);
      setHoveredIndex(-1);
      clearSearch();
      skipNextOpenRef.current = true;
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isMultipleMode, onAddProducts]);

  const handleQuantityChange = useCallback((productId: string, delta: number) => {
    setQuantities(q => ({ ...q, [productId]: Math.max(1, (q[productId] ?? 1) + delta) }));
  }, []);

  const handleAddMultiple = useCallback(() => {
    const items = Array.from(selectedIds)
      .map(id => {
        const product = selectedProductsMap.get(id);
        return product ? { product, quantity: quantities[id] ?? 1 } : null;
      })
      .filter(Boolean) as { product: SelectordeProductosProduct; quantity: number }[];
    if (!items.length) return;
    onAddProducts(items);
    setSelectedIds(new Set());
    setSelectedProductsMap(new Map());
    setQuantities({});
    setSearchTerm('');
    setShowDropdown(false);
    setHoveredIndex(-1);
    clearSearch();
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [selectedIds, quantities, selectedProductsMap, onAddProducts, clearSearch]);

  const resetScanBurst = useCallback(() => {
    const state = scanBurstRef.current;
    if (state.timeoutId) {
      clearTimeout(state.timeoutId);
    }
    state.startTime = 0;
    state.lastTime = 0;
    state.length = 0;
    state.timeoutId = null;
  }, []);

  useEffect(() => {
    return () => {
      resetScanBurst();
    };
  }, [resetScanBurst]);

  const tryScanAndAddToCart = useCallback((rawValue: string): boolean => {
    const normalizedKey = normalizeScanKey(rawValue);
    if (!normalizedKey) {
      return false;
    }

    const matchedProduct = productLookupByCode.get(normalizedKey);
    if (!matchedProduct) {
      return false;
    }

    onAddProducts([{ product: mapProduct(matchedProduct), quantity: 1 }]);
    setSearchTerm('');
    setShowDropdown(false);
    setHoveredIndex(-1);
    clearSearch();
    skipNextOpenRef.current = true;
    requestAnimationFrame(() => inputRef.current?.focus());
    return true;
  }, [clearSearch, mapProduct, onAddProducts, productLookupByCode]);

  const evaluateScanBurst = useCallback(() => {
    const state = scanBurstRef.current;
    if (state.length === 0) {
      return;
    }
    const duration = state.lastTime - state.startTime;
    const candidateValue = inputRef.current?.value ?? '';
    resetScanBurst();
    if (duration > SCAN_BURST_MAX_DURATION_MS) {
      return;
    }
    if (candidateValue.length < SCAN_AUTO_MIN_LENGTH) {
      return;
    }
    if (!looksLikeScanCode(candidateValue)) {
      return;
    }
    tryScanAndAddToCart(candidateValue);
  }, [resetScanBurst, tryScanAndAddToCart]);

  const scheduleScanEvaluation = useCallback(() => {
    const state = scanBurstRef.current;
    if (state.timeoutId) {
      clearTimeout(state.timeoutId);
    }
    state.timeoutId = window.setTimeout(() => {
      evaluateScanBurst();
    }, SCAN_IDLE_THRESHOLD_MS);
  }, [evaluateScanBurst]);

  const trackScanKeystroke = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      resetScanBurst();
      return;
    }
    if (event.key.length !== 1) {
      return;
    }
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const state = scanBurstRef.current;
    if (state.length === 0) {
      state.startTime = now;
    }
    state.lastTime = now;
    state.length += 1;
    scheduleScanEvaluation();
  }, [resetScanBurst, scheduleScanEvaluation]);

  const handleBarcodePaste = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
    resetScanBurst();
    const pastedText = event.clipboardData?.getData('text') ?? '';
    if (!pastedText) {
      return;
    }
    setTimeout(() => {
      const candidateValue = inputRef.current?.value ?? pastedText;
      if (candidateValue.length < SCAN_AUTO_MIN_LENGTH) {
        return;
      }
      if (!looksLikeScanCode(candidateValue)) {
        return;
      }
      tryScanAndAddToCart(candidateValue);
    }, 0);
  }, [resetScanBurst, tryScanAndAddToCart]);

  const handleModeToggle = useCallback(() => {
    setIsMultipleMode(m => !m);
    setSelectedIds(new Set());
    setSelectedProductsMap(new Map());
    setQuantities({});
    setSearchTerm('');
    setShowDropdown(false);
    setHoveredIndex(-1);
  }, []);

  const handleRemoveChip = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setSelectedProductsMap(m => { const nm = new Map(m); nm.delete(id); return nm; });
  }, []);

  const handleCreateClick = useCallback(() => {
    setProductNamePrefill(searchTerm.trim());
    setShowProductModal(true);
  }, [searchTerm]);

  const resetSelectorState = useCallback(() => {
    setSearchTerm('');
    setShowDropdown(false);
    setSelectedIds(new Set());
    setQuantities({});
    setHoveredIndex(-1);
    clearSearch();
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [clearSearch]);

  const handleProductCreated = useCallback(async (productData: ProductInput): Promise<CatalogProduct | void> => {
    
    const entorno = (session as any)?.currentCompany?.configuracionSunatEmpresa?.entornoSunat === 'PRODUCCION'
      ? 'produccion'
      : 'demo';

    const dto: ProductoInputDto = {
      ...productoInputADto(productData, ''),
      precio: (productData as unknown as { precio?: number }).precio ?? 0,
      establecimientoIds: productData.establecimientoIds?.length
        ? productData.establecimientoIds
        : (EstablecimientoId ? [EstablecimientoId] : []),
    };

    try {
      const res = await productosService.crear(dto);
      if (res.ok && res.data) {
        registrarProductoCreadoExitoso({
          entorno,
          origen: 'emision_inline',
          codigo: productData.codigo,
          nombre: productData.nombre,
          categoria: productData.categoria,
          tipoProducto: productData.tipoProducto,
        });
        const mapped = mapBuscarDto(res.data as unknown as ProductoBuscarDto);
        onAddProducts([{ product: mapped, quantity: 1 }]);
        resetSelectorState();
        useOnboardingStore.getState().requestRefresh();
        return res.data as unknown as CatalogProduct;
      }
    } catch {
      // fallback: agrega el producto localmente sin UUID de DB
    }

    const created = addProduct(productData);
    registrarProductoCreadoExitoso({
      entorno,
      origen: 'emision_inline',
      codigo: productData.codigo,
      nombre: productData.nombre,
      categoria: productData.categoria,
      tipoProducto: productData.tipoProducto,
    });
    onAddProducts([{ product: mapProduct(created), quantity: 1 }]);
    resetSelectorState();
    return created;
  }, [addProduct, mapBuscarDto, mapProduct, onAddProducts, resetSelectorState, session]);

  // ── Render ──────────────────────────────────────────────────────────────────
  const selectedArray = Array.from(selectedIds);

  return (
    <>
      <div className="ps-root" ref={containerRef}>

        {/* Topbar: etiqueta + toggle modo múltiple */}
        <div className="ps-topbar">
          <span className="ps-section-label">Agregar producto</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {extraTopbarContent}
            <div
              className="ps-toggle-wrap"
              onClick={handleModeToggle}
              role="switch"
              aria-checked={isMultipleMode}
              tabIndex={0}
              onKeyDown={e => e.key === ' ' && handleModeToggle()}
            >
              <span className={`ps-toggle-text ${isMultipleMode ? 'ps-toggle-text--on' : ''}`}>
                Selección múltiple
              </span>
              <div className={`ps-toggle ${isMultipleMode ? 'ps-toggle--on' : ''}`}>
                <div className="ps-toggle-knob" />
              </div>
            </div>
          </div>
        </div>

        {/* Chips de productos seleccionados (modo múltiple) */}
        {isMultipleMode && selectedArray.length > 0 && (
          <div className="ps-chips">
            {selectedArray.slice(0, 3).map(id => {
              const product = filteredProducts.find(p => p.id === id);
              if (!product) return null;
              const qty = quantities[id] ?? 1;
              return (
                <Badge
                  key={id}
                  variant="blue"
                  noDot
                  size="sm"
                  iconRight={
                    <button
                      id={`selector-productos-chip-quitar-${id}`}
                      name={`selectorProductosChipQuitar${id}`}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleRemoveChip(id); }}
                      aria-label={`Quitar ${product.name}`}
                      style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0, display: 'flex', alignItems: 'center', color: 'inherit' }}
                    >
                      <X size={10} />
                    </button>
                  }
                >
                  <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                    {product.name}
                  </span>
                  {qty > 1 && <span style={{ flexShrink: 0 }}>&nbsp;×{qty}</span>}
                </Badge>
              );
            })}
            {selectedArray.length > 3 && (
              <Badge variant="gray" noDot size="sm">+{selectedArray.length - 3} más</Badge>
            )}
          </div>
        )}

        {/* Input de búsqueda */}
        <div className="ps-input-wrap">
          <input
            id="selector-productos-buscar"
            name="selectorProductosBuscar"
            ref={inputRef}
            className={`ps-input ${(showDropdown && hasResults) || showFavorites ? 'ps-input--open' : ''}`}
            type="text"
            placeholder="Buscar por nombre, código o categoría..."
            value={searchTerm}
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={showDropdown}
            onChange={e => {
              const v = e.target.value;
              setSearchTerm(v);
              setShowDropdown(true);
              setHoveredIndex(-1);
              searchApi(v);
            }}
            onFocus={() => {
              if (skipNextOpenRef.current) { skipNextOpenRef.current = false; return; }
              setShowDropdown(true);
            }}
            onKeyDown={trackScanKeystroke}
            onPaste={handleBarcodePaste}
          />
          <div className="ps-input-actions">
            {/* Tooltip de ayuda */}
            <div style={{ position: 'relative' }}>
              <button
                id="selector-productos-ayuda"
                name="selectorProductosAyuda"
                type="button"
                className="ps-icon-btn"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                aria-label="Ayuda de búsqueda"
              >
                <HelpCircle size={15} />
              </button>
              {showTooltip && (
                <div style={{
                  position: 'absolute', bottom: 'calc(100% + 8px)', right: 0,
                  background: '#1f2937', color: 'white', fontSize: 12,
                  padding: '8px 12px', borderRadius: 8, whiteSpace: 'nowrap',
                  zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,.2)',
                }}>
                  Busca por nombre, código o categoría
                  <span style={{
                    position: 'absolute', top: '100%', right: 10,
                    border: '5px solid transparent', borderTopColor: '#1f2937',
                  }} />
                </div>
              )}
            </div>
            {/* Limpiar / ícono búsqueda */}
            {searchTerm ? (
              <button
                id="selector-productos-limpiar"
                name="selectorProductosLimpiar"
                type="button"
                className="ps-icon-btn"
                onClick={() => { setSearchTerm(''); setShowDropdown(false); clearSearch(); inputRef.current?.focus(); }}
                aria-label="Limpiar búsqueda"
              >
                <X size={16} />
              </button>
            ) : (
              <Search size={16} color="#9ca3af" />
            )}
          </div>
        </div>

        {/* Dropdown de resultados */}
        {showDropdown && hasResults && (
          <div className="ps-dropdown" data-tour-extra={dropdownTourId}>
            {categoryFilterBar}
            {isMultipleMode && selectedArray.length > 0 && (
              <div className="ps-dropdown-header">
                <Check size={13} />
                {selectedArray.length} producto{selectedArray.length !== 1 ? 's' : ''} seleccionado{selectedArray.length !== 1 ? 's' : ''}
              </div>
            )}

            <div className="ps-dropdown-list" role="listbox">
              {filteredProducts.map((product, index) => {
                const isSelected = selectedIds.has(product.id);
                const isHovered  = hoveredIndex === index;
                const inCart     = existingProducts.includes(product.id);
                const qty        = quantities[product.id] ?? 1;
                const isService  = product.tipoProducto === 'SERVICIO';
                const tone       = stockTone(product.stock, isService);

                return (
                  <div
                    key={product.id}
                    ref={el => { itemRefs.current[index] = el; }}
                    className={[
                      'ps-row',
                      isHovered  ? 'ps-row--hover'    : '',
                      isSelected ? 'ps-row--selected' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleSelect(product)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    {isMultipleMode && (
                      <div className={`ps-check ${isSelected ? 'ps-check--on' : ''}`}>
                        {isSelected && <Check size={10} strokeWidth={3} />}
                      </div>
                    )}

                    <div className="ps-stripe" />

                    <div className="ps-row-body">
                      <div className="ps-row-top">
                        <span className="ps-name">{highlightMatch(product.name, searchTerm)}</span>
                        <span className="ps-price">{fmt(product.price)}</span>
                      </div>
                      <div className="ps-row-meta">
                        <span className="ps-badge ps-badge--code">
                          {highlightMatch(product.code, searchTerm)}
                        </span>
                        <Badge
                          noDot
                          size="sm"
                          color={categoryColorMap[product.category.toLowerCase()]}
                          variant="green"
                        >
                          {product.category}
                        </Badge>
                        {tone && (
                          <span className="ps-badge ps-badge--stock" style={{ color: tone.color }}>
                            <span className="ps-dot" style={{ background: tone.color, marginRight: 3 }} />
                            {tone.label}
                          </span>
                        )}
                        <Badge variant="gray" shape="pill" noDot size="sm">
                          {product.priceType ?? 'Precio base'}
                        </Badge>
                        {product.esDetraccion && product.detraccionCodigo && (() => {
                          const entry = CATALOGO_54_DETRACCIONES.find(d => d.codigo === product.detraccionCodigo);
                          return (
                            <Badge variant="teal" shape="pill" noDot size="sm">
                              {product.detraccionCodigo}{entry ? ` — ${entry.descripcion}` : ''}
                            </Badge>
                          );
                        })()}
                        {inCart && (
                          <span className="ps-badge ps-badge--cart">
                            <ShoppingCart size={9} /> En carrito
                          </span>
                        )}
                      </div>
                    </div>

                    {isMultipleMode && isSelected && (
                      <div className="ps-row-right">
                        <div className="ps-qty">
                          <button
                            id={`selector-productos-cantidad-disminuir-${product.id}`}
                            name={`selectorProductosCantidadDisminuir${product.id}`}
                            type="button"
                            className="ps-qty-btn"
                            onClick={e => { e.stopPropagation(); handleQuantityChange(product.id, -1); }}
                            aria-label="Disminuir cantidad"
                          >−</button>
                          <span className="ps-qty-value">{qty}</span>
                          <button
                            id={`selector-productos-cantidad-aumentar-${product.id}`}
                            name={`selectorProductosCantidadAumentar${product.id}`}
                            type="button"
                            className="ps-qty-btn"
                            onClick={e => { e.stopPropagation(); handleQuantityChange(product.id, 1); }}
                            aria-label="Aumentar cantidad"
                          >+</button>
                        </div>
                        <span className="ps-subtotal">{fmt(product.price * qty)}</span>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Crear producto */}
              {searchTerm.trim() && (
                <div className="ps-create-row" onClick={handleCreateClick}>
                  <div className="ps-create-row-icon">
                    <Sparkles size={13} color="#2563eb" />
                  </div>
                  Crear «{searchTerm.trim()}» como nuevo producto
                </div>
              )}
            </div>

            {isMultipleMode && (
              <div className="ps-footer">
                <button
                  id="selector-productos-agregar-multiples-resultados"
                  name="selectorProductosAgregarMultiplesResultados"
                  type="button"
                  className="ps-add-btn"
                  onClick={handleAddMultiple}
                  disabled={selectedArray.length === 0}
                >
                  <ShoppingCart size={15} />
                  Agregar productos
                  {selectedArray.length > 0 && (
                    <span className="ps-add-btn-badge">{selectedArray.length}</span>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Dropdown de favoritos (cuando no hay búsqueda) */}
        {showFavorites && (
          <div className="ps-dropdown" data-tour-extra={dropdownTourId}>
            {categoryFilterBar}
            {isMultipleMode && selectedArray.length > 0 && (
              <div className="ps-dropdown-header">
                <Check size={13} />
                {selectedArray.length} producto{selectedArray.length !== 1 ? 's' : ''} seleccionado{selectedArray.length !== 1 ? 's' : ''}
              </div>
            )}
            <div className="ps-fav-header">
              <Star size={12} className="ps-fav-star" fill="currentColor" />
              Favoritos
            </div>
            <div className="ps-dropdown-list" role="listbox">
              {favoriteProducts.map((product, index) => {
                const isSelected = selectedIds.has(product.id);
                const isHovered  = hoveredIndex === index;
                const inCart     = existingProducts.includes(product.id);
                const qty        = quantities[product.id] ?? 1;
                const isService  = product.tipoProducto === 'SERVICIO';
                const tone       = stockTone(product.stock, isService);

                return (
                  <div
                    key={product.id}
                    ref={el => { itemRefs.current[index] = el; }}
                    className={[
                      'ps-row',
                      isHovered  ? 'ps-row--hover'    : '',
                      isSelected ? 'ps-row--selected' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleSelect(product)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    {isMultipleMode && (
                      <div className={`ps-check ${isSelected ? 'ps-check--on' : ''}`}>
                        {isSelected && <Check size={10} strokeWidth={3} />}
                      </div>
                    )}

                    <div className="ps-stripe" />

                    <div className="ps-row-body">
                      <div className="ps-row-top">
                        <span className="ps-name">
                          <span className="ps-fav-name-star">
                            <Star size={11} className="ps-fav-star" fill="currentColor" style={{ flexShrink: 0 }} />
                            {product.name}
                          </span>
                        </span>
                        <span className="ps-price">{fmt(product.price)}</span>
                      </div>
                      <div className="ps-row-meta">
                        <span className="ps-badge ps-badge--code">{product.code}</span>
                        <Badge
                          noDot
                          size="sm"
                          color={categoryColorMap[product.category.toLowerCase()]}
                          variant="green"
                        >
                          {product.category}
                        </Badge>
                        {tone && (
                          <span className="ps-badge ps-badge--stock" style={{ color: tone.color }}>
                            <span className="ps-dot" style={{ background: tone.color, marginRight: 3 }} />
                            {tone.label}
                          </span>
                        )}
                        <Badge variant="gray" shape="pill" noDot size="sm">
                          {product.priceType ?? 'Precio base'}
                        </Badge>
                        {product.esDetraccion && product.detraccionCodigo && (() => {
                          const entry = CATALOGO_54_DETRACCIONES.find(d => d.codigo === product.detraccionCodigo);
                          return (
                            <Badge variant="teal" shape="pill" noDot size="sm">
                              {product.detraccionCodigo}{entry ? ` — ${entry.descripcion}` : ''}
                            </Badge>
                          );
                        })()}
                        {inCart && (
                          <span className="ps-badge ps-badge--cart">
                            <ShoppingCart size={9} /> En carrito
                          </span>
                        )}
                      </div>
                    </div>

                    {isMultipleMode && isSelected && (
                      <div className="ps-row-right">
                        <div className="ps-qty">
                          <button
                            id={`selector-productos-fav-cantidad-disminuir-${product.id}`}
                            name={`selectorProductosFavCantidadDisminuir${product.id}`}
                            type="button"
                            className="ps-qty-btn"
                            onClick={e => { e.stopPropagation(); handleQuantityChange(product.id, -1); }}
                            aria-label="Disminuir cantidad"
                          >−</button>
                          <span className="ps-qty-value">{qty}</span>
                          <button
                            id={`selector-productos-fav-cantidad-aumentar-${product.id}`}
                            name={`selectorProductosFavCantidadAumentar${product.id}`}
                            type="button"
                            className="ps-qty-btn"
                            onClick={e => { e.stopPropagation(); handleQuantityChange(product.id, 1); }}
                            aria-label="Aumentar cantidad"
                          >+</button>
                        </div>
                        <span className="ps-subtotal">{fmt(product.price * qty)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {isMultipleMode && (
              <div className="ps-footer">
                <button
                  id="selector-productos-agregar-multiples-favoritos"
                  name="selectorProductosAgregarMultiplesFavoritos"
                  type="button"
                  className="ps-add-btn"
                  onClick={handleAddMultiple}
                  disabled={selectedArray.length === 0}
                >
                  <ShoppingCart size={15} />
                  Agregar productos
                  {selectedArray.length > 0 && (
                    <span className="ps-add-btn-badge">{selectedArray.length}</span>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Buscando... */}
        {showDropdown && isSearching && searchTerm.trim().length > 0 && (
          <div className="ps-dropdown" data-tour-extra={dropdownTourId}>
            {categoryFilterBar}
            <div className="ps-no-results">
              <div className="ps-no-results-icon">
                <Search size={22} />
              </div>
              <p className="ps-no-results-title">Buscando productos…</p>
            </div>
          </div>
        )}

        {/* Sin resultados */}
        {showNoResult && (
          <div className="ps-dropdown" data-tour-extra={dropdownTourId}>
            {categoryFilterBar}
            <div className="ps-no-results">
              <div className="ps-no-results-icon">
                <Search size={22} />
              </div>
              {isEmptySearch ? (
                <p className="ps-no-results-title">Sin productos en esta categoría</p>
              ) : (
                <>
                  <p className="ps-no-results-title">Sin resultados para «{searchTerm}»</p>
                  <p className="ps-no-results-sub">Intenta con otro término o crea el producto</p>
                  <button id="selector-productos-crear" name="selectorProductosCrear" type="button" className="ps-create-btn" onClick={handleCreateClick}>
                    <Sparkles size={14} />
                    Crear «{searchTerm.trim()}»
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <ProductModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        onSave={handleProductCreated as any}
        prefillName={productNamePrefill}
        categories={catalogCategories}
        allProducts={catalogProducts}
      />
    </>
  );
};

export default SelectordeProductos;
